import { DispatchStop } from './dispatch-planner.component';

export interface TeamCategoryCount {
  key: string;
  label: string;
  count: number;
}

export interface TeamSummaryMetrics {
  stopCount: number;
  calendarCount: number;
  distance: string;
  duration: string;
  umsatz: number;
  categories: TeamCategoryCount[];
}

const CATEGORY_LABELS: Record<string, string> = {
  kueche: 'Küche',
  moebel: 'Möbel',
  'kueche-rekla': 'Küche Rekla',
  'moebel-rekla': 'Möbel Rekla',
  rekla: 'Rekla',
  sonstiges: 'Sonstiges',
  'montage-lv': 'Montage (LV)',
  'lv-m': 'Liefervorschlag M',
  'lv-mr': 'Liefervorschlag MR',
  'lv-o': 'Liefervorschlag O',
  'lv-other': 'Liefervorschlag'
};

function normalizeForMatch(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\uFFFD/g, '')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ß/g, 'ss');
}

export function getStopCategoryKey(stop: DispatchStop): string {
  if (stop.source === 'delivery-suggestion') {
    if ((stop.montageFlag || '').trim().toUpperCase() === 'J') {
      return 'montage-lv';
    }
    const art = (stop.auftragsart || 'M').trim().toUpperCase();
    if (art === 'M') {
      return 'lv-m';
    }
    if (art === 'MR') {
      return 'lv-mr';
    }
    if (art === 'O') {
      return 'lv-o';
    }
    return 'lv-other';
  }

  const labelNorm = normalizeForMatch(stop.kontingentLabel || '');
  const isRekla = labelNorm.includes('rekla');

  if (stop.kontingent === 'kueche') {
    return isRekla ? 'kueche-rekla' : 'kueche';
  }
  if (stop.kontingent === 'moebel') {
    return isRekla ? 'moebel-rekla' : 'moebel';
  }
  if (isRekla) {
    if (labelNorm.includes('mobel') || labelNorm.includes('moebel') || labelNorm.includes('mbel')) {
      return 'moebel-rekla';
    }
    if (labelNorm.includes('kuch') || labelNorm.includes('kuech') || labelNorm.includes('kche')) {
      return 'kueche-rekla';
    }
    return 'rekla';
  }

  return 'sonstiges';
}

export function getStopCategoryLabel(stop: DispatchStop): string {
  const key = getStopCategoryKey(stop);
  if (CATEGORY_LABELS[key]) {
    return CATEGORY_LABELS[key];
  }
  if (stop.kontingentLabel?.trim()) {
    return stop.kontingentLabel.trim();
  }
  return 'Sonstiges';
}

export function buildTeamCategoryBreakdown(stops: DispatchStop[]): TeamCategoryCount[] {
  const buckets = new Map<string, TeamCategoryCount>();

  for (const stop of stops) {
    const key = getStopCategoryKey(stop);
    const existing = buckets.get(key);
    if (existing) {
      existing.count += 1;
      continue;
    }
    buckets.set(key, {
      key,
      label: CATEGORY_LABELS[key] || getStopCategoryLabel(stop),
      count: 1
    });
  }

  const order = [
    'kueche',
    'moebel',
    'kueche-rekla',
    'moebel-rekla',
    'rekla',
    'montage-lv',
    'lv-m',
    'lv-mr',
    'lv-o',
    'lv-other',
    'sonstiges'
  ];

  return Array.from(buckets.values()).sort((a, b) => {
    const ai = order.indexOf(a.key);
    const bi = order.indexOf(b.key);
    const aRank = ai === -1 ? 999 : ai;
    const bRank = bi === -1 ? 999 : bi;
    if (aRank !== bRank) {
      return aRank - bRank;
    }
    return a.label.localeCompare(b.label, 'de');
  });
}
